<template>
  <div class="relative w-64">
    <!-- Input Field -->
    <div ref="referenceElement" class="relative">
      <input
        v-model="displayValue"
        @click="toggleCalendar"
        @keydown.escape="closeCalendar"
        readonly
        class="w-full px-4 py-2 pr-10 text-sm border border-gray-300 rounded-lg bg-white cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        :placeholder="placeholder"
      />
      <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
        <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z"
          ></path>
        </svg>
      </div>
    </div>

    <!-- Calendar Dropdown Portal -->
    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="popperElement"
        class="bg-white border border-gray-200 rounded-lg shadow-xl w-80"
        style="z-index: 10031"
      >
        <!-- Calendar Header -->
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <button
            @click="previousMonth"
            class="p-1 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <svg
              class="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              ></path>
            </svg>
          </button>

          <div class="flex items-center space-x-2">
            <select
              v-model="currentMonth"
              @change="updateCalendar"
              class="text-sm font-medium text-gray-900 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
            >
              <option v-for="(month, index) in months" :key="index" :value="index">
                {{ month }}
              </option>
            </select>

            <select
              v-model="currentYear"
              @change="updateCalendar"
              class="text-sm font-medium text-gray-900 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
            >
              <option v-for="year in years" :key="year" :value="year">
                {{ year }}
              </option>
            </select>
          </div>

          <button
            @click="nextMonth"
            class="p-1 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <svg
              class="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              ></path>
            </svg>
          </button>
        </div>

        <!-- Calendar Grid -->
        <div class="p-4">
          <!-- Day Headers -->
          <div class="grid grid-cols-7 gap-1 mb-2">
            <div
              v-for="day in dayHeaders"
              :key="day"
              class="text-xs font-medium text-gray-500 text-center py-2"
            >
              {{ day }}
            </div>
          </div>

          <!-- Calendar Days -->
          <div class="grid grid-cols-7 gap-1">
            <button
              v-for="date in calendarDays"
              :key="`${date.year}-${date.month}-${date.day}`"
              @click="selectDate(date)"
              :disabled="date.disabled"
              :class="[
                'h-8 w-8 text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500',
                {
                  'text-gray-300 cursor-not-allowed': date.disabled,
                  'text-gray-400 hover:bg-gray-100': !date.isCurrentMonth && !date.disabled,
                  'text-gray-900 hover:bg-gray-100':
                    date.isCurrentMonth && !date.selected && !date.isToday && !date.disabled,
                  'bg-blue-100 text-blue-600 font-medium': date.isToday && !date.selected,
                  'bg-blue-600 text-white font-medium hover:bg-blue-700': date.selected,
                },
              ]"
            >
              {{ date.day }}
            </button>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-between px-4 py-3 border-t border-gray-200">
          <button
            @click="selectToday"
            class="text-sm text-blue-600 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
          >
            Today
          </button>
          <button
            @click="clearDate"
            class="text-sm text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 rounded px-2 py-1"
          >
            Clear
          </button>
        </div>
      </div>

      <!-- Backdrop -->
      <div
        v-if="isOpen"
        @click="closeCalendar"
        class="fixed inset-0 bg-black bg-opacity-10"
        style="z-index: 10030"
      ></div>
    </Teleport>
  </div>
</template>

<script setup>
import { createPopper } from '@popperjs/core';
import { format, isAfter, isBefore, isSameDay } from 'date-fns';
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';

// Props
const props = defineProps({
  modelValue: {
    type: [Date, String, null],
    default: null,
  },
  placeholder: {
    type: String,
    default: 'Select date',
  },
  format: {
    type: String,
    default: 'MM/dd/yyyy',
  },
  minDate: {
    type: Date,
    default: null,
  },
  maxDate: {
    type: Date,
    default: null,
  },
});

// Emits
const emit = defineEmits(['update:modelValue']);

// Reactive data
const isOpen = ref(false);
const currentMonth = ref(new Date().getMonth());
const currentYear = ref(new Date().getFullYear());

// Add these new reactive references after the existing ones
const referenceElement = ref(null);
const popperElement = ref(null);
let popperInstance = null;

// Constants
const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const dayHeaders = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// Computed properties
const selectedDate = computed(() => {
  if (!props.modelValue) return null;
  return props.modelValue instanceof Date ? props.modelValue : new Date(props.modelValue);
});

const displayValue = computed(() => {
  if (!selectedDate.value) return '';
  return format(selectedDate.value, props.format);
});

const years = computed(() => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 50; i <= currentYear + 50; i++) {
    years.push(i);
  }
  return years;
});

const calendarDays = computed(() => {
  const firstDay = new Date(currentYear.value, currentMonth.value, 1);
  const lastDay = new Date(currentYear.value, currentMonth.value + 1, 0);
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - firstDay.getDay());

  const days = [];
  const today = new Date();

  for (let i = 0; i < 42; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);

    const isCurrentMonth = date.getMonth() === currentMonth.value;
    const isToday = isSameDay(date, today);
    const isSelected = selectedDate.value && isSameDay(date, selectedDate.value);
    const isDisabled = isDateDisabled(date);

    days.push({
      day: date.getDate(),
      month: date.getMonth(),
      year: date.getFullYear(),
      date: new Date(date),
      isCurrentMonth,
      isToday,
      selected: isSelected,
      disabled: isDisabled,
    });
  }

  return days;
});

// Methods
const createPopperInstance = async () => {
  await nextTick();

  if (referenceElement.value && popperElement.value) {
    popperInstance = createPopper(referenceElement.value, popperElement.value, {
      placement: 'bottom-start',
      strategy: 'fixed',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 4],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            padding: 8,
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
          },
        },
      ],
    });
  }
};

const destroyPopperInstance = () => {
  if (popperInstance) {
    popperInstance.destroy();
    popperInstance = null;
  }
};

const toggleCalendar = async () => {
  if (isOpen.value) {
    closeCalendar();
  } else {
    isOpen.value = true;
    await createPopperInstance();
  }
};

const closeCalendar = () => {
  isOpen.value = false;
  destroyPopperInstance();
};

const selectDate = (dateObj) => {
  if (dateObj.disabled) return;

  const newDate = new Date(dateObj.year, dateObj.month, dateObj.day);
  emit('update:modelValue', newDate);
  closeCalendar();
};

const selectToday = () => {
  const today = new Date();
  if (!isDateDisabled(today)) {
    emit('update:modelValue', today);
    currentMonth.value = today.getMonth();
    currentYear.value = today.getFullYear();
  }
  closeCalendar();
};

const clearDate = () => {
  emit('update:modelValue', null);
  closeCalendar();
};

const previousMonth = () => {
  if (currentMonth.value === 0) {
    currentMonth.value = 11;
    currentYear.value--;
  } else {
    currentMonth.value--;
  }
};

const nextMonth = () => {
  if (currentMonth.value === 11) {
    currentMonth.value = 0;
    currentYear.value++;
  } else {
    currentMonth.value++;
  }
};

const updateCalendar = () => {
  // Calendar will automatically update due to reactive properties
};

const isDateDisabled = (date) => {
  if (props.minDate && isBefore(date, props.minDate)) return true;
  if (props.maxDate && isAfter(date, props.maxDate)) return true;
  return false;
};

const handleClickOutside = (event) => {
  if (
    isOpen.value &&
    !referenceElement.value?.contains(event.target) &&
    !popperElement.value?.contains(event.target)
  ) {
    closeCalendar();
  }
};

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside);

  // Set initial month/year based on selected date
  if (selectedDate.value) {
    currentMonth.value = selectedDate.value.getMonth();
    currentYear.value = selectedDate.value.getFullYear();
  }
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  destroyPopperInstance();
});
</script>
